import React, { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLabelPrint } from "@/context/LabelPrintContext";
import { formatDate, formatMedida } from "@/lib/format";
import { sharePdf, printPdf, logAcesso } from "@/lib/share";
import { ArrowDownToLine, X, Loader2, Share2, Printer, Tag } from "lucide-react";
import { toast } from "sonner";

/**
 * Modal de detalhe do documento de entrada.
 * Compartilhado entre EntradasSection (lista em /entrada) e RoloTimelineModal (link "Ver documento →").
 */
export function EntradaDetalheModal({ entradaId, open, onClose, zClass = "z-50" }) {
  const { hasPerm } = useAuth();
  const canSharePdf = hasPerm("entrada:compartilhar_pdf");
  const canPrintDoc = hasPerm("entrada:imprimir_documento");
  const canPrintLabels = hasPerm("entrada:imprimir_etiquetas");
  const { printLabels } = useLabelPrint();
  const [detalhe, setDetalhe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [printingLabels, setPrintingLabels] = useState(false);

  useEffect(() => {
    if (!open || !entradaId) {
      setDetalhe(null);
      return;
    }
    setLoading(true);
    api
      .get(`/entradas/${entradaId}`)
      .then((r) => setDetalhe(r.data))
      .catch((e) => {
        {const _m=apiError(e); if(_m) toast.error(_m);};
        onClose && onClose();
      })
      .finally(() => setLoading(false));
  }, [open, entradaId, onClose]);

  const compartilhar = async () => {
    if (!detalhe) return;
    setSharing(true);
    try {
      await sharePdf(
        `/entradas/${detalhe.id}/pdf`,
        `Entrada - ${detalhe.codigo}.pdf`,
        `Entrada ${detalhe.codigo}`
      );
      logAcesso({ tipo: "ent_compartilhado", documento_tipo: "entrada", documento_id: detalhe.id, documento_codigo: detalhe.codigo });
    } finally {
      setSharing(false);
    }
  };

  const imprimirDocumento = () => {
    if (!detalhe) return;
    printPdf(`/entradas/${detalhe.id}/pdf`);
    logAcesso({ tipo: "ent_impresso_doc", documento_tipo: "entrada", documento_id: detalhe.id, documento_codigo: detalhe.codigo });
  };

  const imprimirEtiquetas = async () => {
    if (!detalhe) return;
    setPrintingLabels(true);
    try {
      const res = await api.get(`/entradas/${detalhe.id}/rolos`);
      const rolos = res.data || [];
      if (!rolos.length) {
        toast.error("Este documento não tem rolos para imprimir.");
        return;
      }
      printLabels(rolos);
      logAcesso({ tipo: "ent_impresso_etiquetas", documento_tipo: "entrada", documento_id: detalhe.id, documento_codigo: detalhe.codigo, qtd_rolos: rolos.length });
    } catch (err) {
      const _m = apiError(err);
      if (_m) toast.error(_m);
    } finally {
      setPrintingLabels(false);
    }
  };

  const totaisDoc = useMemo(() => {
    if (!detalhe) return { kg: 0, m: 0 };
    return (detalhe.itens || []).reduce(
      (acc, i) => {
        const v = Number(i.peso) || 0;
        if ((i.unidade || "kg") === "m") acc.m += v;
        else acc.kg += v;
        return acc;
      },
      { kg: 0, m: 0 }
    );
  }, [detalhe]);

  const itensOrdenados = useMemo(() => {
    if (!detalhe) return [];
    return [...(detalhe.itens || [])].sort(
      (a, b) =>
        String(a.tipo || "").localeCompare(String(b.tipo || ""), "pt-BR") ||
        String(a.cor || "").localeCompare(String(b.cor || ""), "pt-BR") ||
        String(a.codigo || "").localeCompare(String(b.codigo || ""), "pt-BR")
    );
  }, [detalhe]);

  if (!open) return null;

  return (
    <div
      className={`fixed inset-0 flex items-center justify-center bg-black/50 p-4 ${zClass}`}
      onClick={onClose}
      data-testid="entrada-detalhe-modal"
    >
      <div
        className="max-h-[88vh] w-full max-w-2xl overflow-y-auto rounded-md bg-white shadow-lg"
        onClick={(ev) => ev.stopPropagation()}
        data-testid="entrada-detalhe"
      >
        <div className="rounded-t-md bg-green-700 px-5 py-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-md bg-white/15">
                <ArrowDownToLine size={22} />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-green-100">
                  Documento de Entrada
                </div>
                <div
                  className="font-mono text-2xl font-bold"
                  data-testid="entrada-detalhe-codigo"
                >
                  {detalhe ? detalhe.codigo : "…"}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="entrada-detalhe-close"
              className="rounded-md p-1 text-white/80 hover:bg-white/10"
            >
              <X size={22} />
            </button>
          </div>
          {detalhe && (
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-green-100">
              {detalhe.data && (
                <span>
                  <span className="text-green-200">Data:</span>{" "}
                  <strong className="text-white">{formatDate(detalhe.data)}</strong>
                </span>
              )}
              {detalhe.criado_por_nome && (
                <span>
                  <span className="text-green-200">Lançado por:</span>{" "}
                  <strong className="text-white">{detalhe.criado_por_nome}</strong>
                </span>
              )}
              {detalhe.modo && (
                <span>
                  <span className="text-green-200">Modo:</span>{" "}
                  <strong className="text-white">
                    {detalhe.modo === "lote"
                      ? "Lote"
                      : detalhe.modo === "misto"
                        ? "Misto"
                        : "Individual"}
                  </strong>
                </span>
              )}
              {detalhe.nota_fiscal && (
                <span>
                  <span className="text-green-200">NF:</span>{" "}
                  <strong className="text-white">{detalhe.nota_fiscal}</strong>
                </span>
              )}
              {(detalhe.fornecedores || []).length > 0 && (
                <span>
                  <span className="text-green-200">Fornecedor(es):</span>{" "}
                  <strong className="text-white">
                    {(detalhe.fornecedores || []).join(", ")}
                  </strong>
                </span>
              )}
            </div>
          )}
        </div>

        <div className="p-5">
          {loading || !detalhe ? (
            <div className="flex justify-center py-10 text-slate-400">
              <Loader2 className="animate-spin" />
            </div>
          ) : (
            <>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-semibold text-slate-700">
                  Rolos que entraram ({detalhe.total_rolos || 0})
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {canSharePdf && (
                    <button
                      type="button"
                      onClick={compartilhar}
                      disabled={sharing}
                      data-testid="entrada-detalhe-share"
                      className="flex items-center gap-1 rounded-md border-2 border-green-200 bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700 hover:bg-green-100 disabled:opacity-50"
                    >
                      {sharing ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Share2 size={14} />
                      )}
                      Compartilhar
                    </button>
                  )}
                  {canPrintDoc && (
                    <button
                      type="button"
                      onClick={imprimirDocumento}
                      data-testid="entrada-detalhe-print-doc"
                      className="flex items-center gap-1 rounded-md border-2 border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                    >
                      <Printer size={14} /> Imprimir documento
                    </button>
                  )}
                  {canPrintLabels && (
                    <button
                      type="button"
                      onClick={imprimirEtiquetas}
                      disabled={printingLabels}
                      data-testid="entrada-detalhe-print-labels"
                      className="flex items-center gap-1 rounded-md border-2 border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                    >
                      {printingLabels ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Tag size={14} />
                      )}
                      Imprimir etiquetas
                    </button>
                  )}
                </div>
              </div>
              <ul
                className="divide-y divide-slate-100 rounded-md border-2 border-slate-100"
                data-testid="entrada-itens"
              >
                {itensOrdenados.map((i) => (
                  <li
                    key={i.rolo_id}
                    className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
                  >
                    <span className="min-w-0">
                      <span className="font-mono text-xs text-slate-400">
                        {i.codigo}{" "}
                      </span>
                      {i.tipo ? `${i.tipo} ${i.cor}` : i.cor}{" "}
                      <span className="text-slate-400">({i.fornecedor_nome})</span>
                    </span>
                    <span className="font-mono font-semibold text-slate-800">
                      {formatMedida(i.peso, i.unidade)}
                    </span>
                  </li>
                ))}
              </ul>

              {(totaisDoc.kg > 0 || totaisDoc.m > 0) && (
                <div
                  data-testid="entrada-detalhe-total"
                  className="mt-3 flex flex-wrap items-center justify-end gap-x-5 gap-y-1 rounded-md border-2 border-green-700 bg-green-700 px-4 py-3 text-white"
                >
                  {totaisDoc.kg > 0 && (
                    <div className="flex items-baseline gap-2" data-testid="entrada-total-kg">
                      <span className="text-xs font-medium uppercase tracking-wide text-green-200">
                        Total:
                      </span>
                      <span className="font-mono text-lg font-bold">
                        {totaisDoc.kg.toFixed(2).replace(".", ",")} kg
                      </span>
                    </div>
                  )}
                  {totaisDoc.m > 0 && (
                    <div className="flex items-baseline gap-2" data-testid="entrada-total-m">
                      <span className="text-xs font-medium uppercase tracking-wide text-green-200">
                        Total:
                      </span>
                      <span className="font-mono text-lg font-bold">
                        {totaisDoc.m.toFixed(2).replace(".", ",")} m
                      </span>
                    </div>
                  )}
                </div>
              )}

              {detalhe.localizacao && (
                <div className="mt-3 rounded-md bg-slate-50 p-2 text-sm text-slate-600">
                  <strong>Localização:</strong> {detalhe.localizacao}
                </div>
              )}
              {detalhe.observacao && (
                <div className="mt-2 rounded-md bg-slate-50 p-2 text-sm text-slate-600">
                  <strong>Obs.:</strong> {detalhe.observacao}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
